/**
 * useSimpleTableData
 *
 * Orchestrates field computation, filtering/sorting, and pagination for SimpleTable.
 * Delegates to:
 *   - useSimpleTableFields  → visibleFields, searchableFields
 *   - useSimpleTableFiltering → processedData (filtered + sorted)
 *
 * Public API is unchanged — callers receive the same return shape as before.
 */

import { useMemo } from "react";
import type { BaseEntity, SimpleTableConfig } from "../types";
import { useFilterContext } from "@/contexts/FilterContext";
import { useSimpleTableFields } from "./useSimpleTableFields";
import { useSimpleTableFiltering } from "./useSimpleTableFiltering";
import { paginateRows } from "./paginateRows";

interface UseSimpleTableDataProps<TData extends BaseEntity> {
  config: SimpleTableConfig<TData>;
  data: TData[];
  state: ReturnType<typeof import("./useSimpleTableState").useSimpleTableState<TData>>;
  hooks: ReturnType<typeof import("./useSimpleTableHooks").useSimpleTableHooks>;
  isDataPreFiltered: boolean;
  propTotalItems?: number;
  visibleColumns: Set<string>;
  /**
   * When true the table renders the full row set on purpose (infinite-scroll
   * accumulation or active grouping) and pagination windowing must be skipped.
   */
  disableClientSlice?: boolean;
}

export function useSimpleTableData<TData extends BaseEntity>({
  config,
  data,
  state,
  hooks,
  isDataPreFiltered,
  propTotalItems,
  visibleColumns,
  disableClientSlice,
}: UseSimpleTableDataProps<TData>) {
  const {
    sortField,
    sortDirection,
    sortColumns,
    columnOrder,
    effectiveCurrentPage,
    effectivePageSize,
  } = state;

  const { filters } = useFilterContext<TData>();
  const { filterFieldsByRole } = hooks;

  // ── Fields ────────────────────────────────────────────────────────────────
  const { visibleFields, searchableFields } = useSimpleTableFields({
    config,
    visibleColumns,
    columnOrder,
    filterFieldsByRole,
  });

  // ── Filtering + sorting ───────────────────────────────────────────────────
  const { processedData } = useSimpleTableFiltering({
    config,
    data,
    searchTerm: filters.search ?? "",
    simpleFilters: filters.simple,
    sortField,
    sortDirection,
    sortColumns,
    searchableFields,
    isDataPreFiltered,
  });

  // ── Pagination ────────────────────────────────────────────────────────────

  const paginatedData = useMemo(
    () =>
      paginateRows({
        rows: processedData,
        currentPage: effectiveCurrentPage,
        pageSize: effectivePageSize,
        totalItems: propTotalItems,
        disableClientSlice,
      }),
    [processedData, effectiveCurrentPage, effectivePageSize, propTotalItems, disableClientSlice]
  );

  const totalPages = useMemo(() => {
    if (propTotalItems !== undefined) {
      return Math.ceil(propTotalItems / effectivePageSize);
    }
    return Math.ceil(processedData.length / effectivePageSize);
  }, [propTotalItems, effectivePageSize, processedData.length]);

  return {
    visibleFields,
    searchableFields,
    processedData,
    paginatedData,
    totalPages,
  };
}
